/**
 * Created: 2026-05-05
 * Initial commit: Add coupon templates management page
 *
 * Page for listing, creating, and editing coupon templates for a site.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { Field, type ISchema, message, useForm } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import QrCodeIcon from '@mui/icons-material/QrCode'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useState } from 'react'
import CouponPosterDialog from '../components/CouponPosterDialog'
import NumberInput from '../components/NumberInput'
import {
  CouponCategory,
  CouponTemplatesDocument,
  type CouponTemplatesQuery,
  CreateCouponTemplateDocument,
  SiteRole,
  SportCategory,
  UpdateCouponTemplateDocument,
} from '../gql/graphql'
import { useRole } from '../hooks'
import { COUPON_CATEGORY_LABEL, SPORT_LABEL } from '../lib/domainLabels'

// Created: 2026-05-05 | Initial commit: Add coupon templates management page
gql`
  query CouponTemplates($siteId: UUID!) {
    couponTemplates(siteId: $siteId) {
      id
      name
      description
      category
      discountAmount
      sport
      validDays
      maxIssueCount
      issuedCount
      createdBy
      createTime
    }
  }

  mutation CreateCouponTemplate($templateInput: CreateCouponTemplateInput!) {
    createCouponTemplate(templateInput: $templateInput) {
      id
      name
      description
      category
      discountAmount
      sport
      validDays
      maxIssueCount
      issuedCount
      createdBy
      createTime
    }
  }

  mutation UpdateCouponTemplate(
    $couponTemplateId: ID!
    $templateInput: UpdateCouponTemplateInput!
  ) {
    updateCouponTemplate(
      couponTemplateId: $couponTemplateId
      templateInput: $templateInput
    ) {
      ... on CouponTemplate {
        id
        name
        description
        category
        discountAmount
        sport
        validDays
        maxIssueCount
        issuedCount
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

type CouponTemplate = NonNullable<CouponTemplatesQuery['couponTemplates']>[number]

interface CouponFormValues {
  name: string
  description: string
  category: CouponCategory
  discountAmount: number | null
  sport: SportCategory | ''
  validDays: number
  maxIssueCount: number | null
}

const schema: ISchema = {
  rules: {
    name: 'required|max:20',
    description: 'max:200',
    validDays: ['required', 'min:1'],
  },
  messages: {
    'required.name': '请填写模板名称',
    'max.name': '名称不能超过20字',
    'max.description': '描述不能超过200字',
    'required.validDays': '请填写有效天数',
    'min.validDays': '有效天数须为正整数',
  },
  validate: ({ category, sport }: CouponFormValues, errors) => {
    if (category !== CouponCategory.FixedDiscount && !sport) {
      errors.add('sport', '请选择运动类型')
    }
  },
}

interface CouponDialogProps {
  siteId: string
  template: CouponTemplate | null
  readonly?: boolean
  onClose: () => void
  onSaved: () => void
}

function CouponDialog({
  siteId,
  template,
  readonly,
  onClose,
  onSaved,
}: CouponDialogProps) {
  const [createTemplate] = useMutation(CreateCouponTemplateDocument)
  const [updateTemplate] = useMutation(UpdateCouponTemplateDocument)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const isEditMode = template != null

  const form = useForm<CouponFormValues>(
    {
      name: template?.name ?? '',
      description: template?.description ?? '',
      category: template?.category ?? CouponCategory.FreeSlot,
      discountAmount: template?.discountAmount ?? null,
      sport: template?.sport ?? '',
      validDays: template?.validDays ?? 60,
      maxIssueCount: template?.maxIssueCount ?? null,
    },
    schema,
    readonly,
  )

  const needsDiscount = form.values.category === CouponCategory.FixedDiscount
  const needsSport =
    !isEditMode && form.values.category !== CouponCategory.FixedDiscount

  const handleSubmit = async () => {
    if (form.validate(false)) return

    setSubmitting(true)
    setSubmitError('')
    try {
      const {
        name,
        description,
        category,
        discountAmount,
        sport,
        validDays,
        maxIssueCount,
      } = form.values
      if (isEditMode) {
        const { data } = await updateTemplate({
          variables: {
            couponTemplateId: template.id,
            templateInput: {
              name: (name ?? '').trim(),
              description: description.trim() || null,
              validDays: validDays ?? 30,
              discountAmount: needsDiscount ? (discountAmount ?? null) : null,
              maxIssueCount: maxIssueCount ?? null,
            },
          },
        })
        const result = data?.updateCouponTemplate
        if (result?.__typename === 'GraphqlRequestError') {
          setSubmitError(result.message ?? '更新失败')
          return
        }
        message.success('模板已更新', { autoHide: 2000 })
      } else {
        await createTemplate({
          variables: {
            templateInput: {
              siteId,
              name: (name ?? '').trim(),
              description: description.trim() || null,
              category: category as never,
              discountAmount: needsDiscount ? (discountAmount ?? null) : null,
              sport: sport || null,
              validDays: validDays ?? 30,
              maxIssueCount: maxIssueCount ?? null,
            },
          },
        })
        message.success('模板已创建', { autoHide: 2000 })
      }
      onSaved()
    } catch (err) {
      setSubmitError(`操作失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} disableRestoreFocus maxWidth="xs" fullWidth>
      <DialogTitle>
        {isEditMode ? (readonly ? '查看模板' : '编辑模板') : '新建模板'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEditMode && (
            <FormControl size="small">
              <FormLabel required sx={{ fontSize: '0.75rem' }}>
                类型
              </FormLabel>
              <RadioGroup
                row
                value={form.values.category}
                onChange={(e) => {
                  const newCategory = e.target.value as CouponCategory
                  form.update({ category: newCategory })
                  if (newCategory === CouponCategory.FixedDiscount) {
                    form.update({ discountAmount: null, sport: '' })
                  } else {
                    form.update({ discountAmount: null })
                  }
                }}
              >
                {(
                  [
                    CouponCategory.FreeSlot,
                    CouponCategory.FreeSignup,
                    CouponCategory.FixedDiscount,
                  ] as const
                ).map((cat) => (
                  <FormControlLabel
                    key={cat}
                    value={cat}
                    control={<Radio size="small" />}
                    label={COUPON_CATEGORY_LABEL[cat]}
                    slotProps={{ typography: { variant: 'body2' } }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}

          {needsDiscount && (
            <Field
              form={form}
              name="discountAmount"
              label="折扣金额 (¥)"
              required
              fullWidth
              slotProps={{
                input: {
                  inputComponent: NumberInput,
                  inputProps: {
                    min: 0.01,
                    max: 100,
                    decimalScale: 2,
                    fixedDecimalScale: true,
                  },
                },
                inputLabel: { shrink: true },
              }}
            />
          )}

          {needsSport && (
            <Field
              select
              form={form}
              name="sport"
              label="适用运动"
              required
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            >
              <MenuItem value={SportCategory.Tennis}>网球</MenuItem>
              <MenuItem value={SportCategory.Pickleball}>匹克球</MenuItem>
            </Field>
          )}

          <Field
            form={form}
            name="validDays"
            label="有效天数"
            required
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 365, decimalScale: 0 },
              },
              inputLabel: { shrink: true },
            }}
          />

          {(!isEditMode || template?.maxIssueCount != null) && (
            <Field
              form={form}
              name="maxIssueCount"
              label="发放上限（留空表示不限）"
              required={isEditMode}
              fullWidth
              slotProps={{
                input: {
                  inputComponent: NumberInput,
                  inputProps: { min: 1, decimalScale: 0, max: 999 },
                },
                inputLabel: { shrink: true },
              }}
            />
          )}

          <Field
            form={form}
            name="name"
            label="显示名称"
            required
            fullWidth
            slotProps={{ htmlInput: { maxLength: 20 }, inputLabel: { shrink: true } }}
          />

          <Field
            form={form}
            name="description"
            label="使用说明"
            required={false}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            slotProps={{ htmlInput: { maxLength: 200 }, inputLabel: { shrink: true } }}
            helperText={`${(form.values.description ?? '').length}/200`}
          />

          {submitError && (
            <Typography variant="body2" color="error">
              {submitError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readonly ? '关闭' : '取消'}</Button>
        {!readonly && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {isEditMode ? '保存' : '创建'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

const columns: GridColDef<CouponTemplate>[] = [
  { field: 'name', headerName: '名称', width: 200, headerAlign: 'center' },
  {
    field: 'category',
    headerName: '类型',
    width: 100,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: CouponCategory) => COUPON_CATEGORY_LABEL[value] ?? value,
  },
  {
    field: 'discountAmount',
    headerName: '折扣金额',
    width: 90,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: number | null) => (value != null ? `¥${value}` : '—'),
  },
  {
    field: 'sport',
    headerName: '运动',
    width: 80,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: SportCategory | null) =>
      value ? (SPORT_LABEL[value] ?? value) : '不限',
  },
  {
    field: 'validDays',
    headerName: '有效天数',
    width: 90,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: number) => `${value} 天`,
  },
  {
    field: 'maxIssueCount',
    headerName: '发放上限',
    width: 90,
    headerAlign: 'center',
    align: 'center',
    valueFormatter: (value: number | null) => (value != null ? value : '不限'),
  },
  {
    field: 'issuedCount',
    headerName: '已发放',
    width: 90,
    headerAlign: 'center',
    align: 'center',
    valueGetter: (_value, row) =>
      row.maxIssueCount != null
        ? `${row.issuedCount} / ${row.maxIssueCount}`
        : row.issuedCount,
  },
  {
    field: 'actions',
    headerName: '操作',
    width: 60,
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    renderCell: () => null, // replaced per-row via onCellClick pattern below
  },
]

export default function CouponTemplatesPage() {
  const { currentSite } = useRole()
  const { data, loading, error, refetch } = useQuery(CouponTemplatesDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })

  const [dialogTemplate, setDialogTemplate] = useState<
    CouponTemplate | null | undefined
  >(undefined)
  const [posterTemplate, setPosterTemplate] = useState<CouponTemplate | null>(null)

  const handleSaved = async () => {
    await refetch()
    setDialogTemplate(undefined)
  }

  const isSuperAdmin = currentSite?.roles.includes(SiteRole.SuperAdmin) ?? false

  const cols: GridColDef<CouponTemplate>[] = [
    ...columns.filter((c) => c.field !== 'actions'),
    {
      field: 'actions',
      headerName: '操作',
      width: 90,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setDialogTemplate(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          {row.maxIssueCount != null && (
            <Tooltip title="下载海报">
              <IconButton
                size="small"
                color="primary"
                onClick={() => setPosterTemplate(row)}
              >
                <QrCodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  const templates = data?.couponTemplates ?? []

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        优惠券模板
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetch()}>
          刷新
        </Button>
        {isSuperAdmin && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogTemplate(null)}
          >
            新建模板
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <DataGrid
        rows={templates}
        columns={cols}
        loading={loading}
        autoHeight
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
      />

      {dialogTemplate !== undefined && currentSite && (
        <CouponDialog
          siteId={currentSite.id}
          template={dialogTemplate}
          readonly={!isSuperAdmin}
          onClose={() => setDialogTemplate(undefined)}
          onSaved={handleSaved}
        />
      )}

      {posterTemplate && currentSite && (
        <CouponPosterDialog
          onClose={() => setPosterTemplate(null)}
          source={{
            kind: 'template',
            id: posterTemplate.id,
            siteId: currentSite.id,
            info: {
              name: posterTemplate.name,
              category: posterTemplate.category,
              sport: posterTemplate.sport,
              discountAmount: posterTemplate.discountAmount,
            },
          }}
        />
      )}
    </Box>
  )
}
